/**
 * Mazlan Gainsight tracking utility.
 *
 * The chat UI (textarea, prompt-suggestion chips) is portaled out to
 * document.body by @ideagen/helix-react-ai, so we cannot rely on React/DOM
 * ancestry. The tracker matches the library's stable `data-id` selectors on
 * a delegated `document` listener instead.
 *
 * Guard: all events are gated by `window.aptrinsic` (Gainsight PX).
 * Global context (agentKey, agentName, version, environment, sessionId,
 * mazlanUiVersion) is set via `aptrinsic('set', 'globalContext')` only while the
 * Mazlan dialog is open. Gainsight PX attaches it to every event while it is set,
 * so it is removed as soon as the dialog closes.
 *
 * Reference: CSASSN-2521 / Assurance mazlanTracking.js (PR #2230).
 */

// -- Constants ----------------------------------------------------------------

const MAZLAN_AGENT_KEY = 'mazlan-policyconnect';
const MAZLAN_AGENT_NAME = 'Mazlan';
// Static agent version — PolicyConnect's agent URL has no version suffix (unlike
// ASSN's `_v2/stream` pattern). Update this when the agent is versioned.
// mazlanUiVersion is read at runtime from the `data-mazlan-ui-version` attribute
// set automatically by @ideagen/helix-react-ai (currently v4.1.0).
const MAZLAN_AGENT_VERSION = '1.0.0';

// -- Gainsight helpers --------------------------------------------------------

// Fire a Gainsight event only when PX tracking is available.
const logGainsightEvent = (eventName, payload = {}) => {
  if (window.aptrinsic) {
    window.aptrinsic('track', eventName, payload);
  }
};

// -- Mazlan UI version --------------------------------------------------------

// Read the helix-react-ai version from the DraggableContainer root element.
const getMazlanUiVersion = () => {
  const root = document.querySelector('[data-mazlan-ui-version]');
  return root?.getAttribute('data-mazlan-ui-version') ?? null;
};

// -- Global context -----------------------------------------------------------

const GLOBAL_CONTEXT_KEYS = [
  'agentKey',
  'agentName',
  'version',
  'environment',
  'sessionId',
  'mazlanUiVersion'
];

const setupGlobalContext = (sessionId) => {
  const context = {
    agentKey: MAZLAN_AGENT_KEY,
    agentName: MAZLAN_AGENT_NAME,
    version: MAZLAN_AGENT_VERSION,
    environment: process.env.APP_ENV || 'unknown',
    sessionId: sessionId || null,
    mazlanUiVersion: getMazlanUiVersion()
  };

  // Register with Gainsight PX so these properties are automatically
  // attached to all future events (custom + auto-tracked).
  if (window.aptrinsic) {
    window.aptrinsic('set', 'globalContext', context);
  }
};

const teardownGlobalContext = () => {
  if (window.aptrinsic) {
    window.aptrinsic('remove', 'globalContext', GLOBAL_CONTEXT_KEYS);
  }
};

// -- Main entry point ---------------------------------------------------------

/**
 * Attach document-level Mazlan Gainsight tracking listeners while the dialog is open.
 * When closed, any existing global context is removed (e.g. one left before a reload).
 * Returns a cleanup function that removes all listeners.
 */
export const attachMazlanTracking = ({ open, hasMazlanAdminFeaturesAccess, sessionId }) => {
  if (!open) {
    teardownGlobalContext();
    return () => {};
  }

  setupGlobalContext(sessionId);

  // On open, helix-react-ai renders its root (which carries data-mazlan-ui-version)
  // after this runs, so re-register the context once the root appears.
  let uiVersionObserver = null;
  if (!getMazlanUiVersion()) {
    uiVersionObserver = new MutationObserver(() => {
      if (!getMazlanUiVersion()) return;
      uiVersionObserver.disconnect();
      setupGlobalContext(sessionId);
    });
    uiVersionObserver.observe(document.body, { childList: true, subtree: true });
  }

  // Keyboard Enter submit — mirrors the library's own guard:
  // only fires when the textarea has non-empty text and the agent is not
  // streaming a response (send button disabled during streaming).
  const trackEnterKeySubmit = (event) => {
    if (event.key !== 'Enter' || event.shiftKey || event.isComposing) return;
    const textarea = event.target.closest?.('[data-id="message-input-textarea"]');
    if (!textarea) return;

    const text = textarea.value?.trim();
    if (!text) return;

    const sendButton = document.querySelector('[data-id="message-input-primary"]');
    if (sendButton?.disabled) return;
    logGainsightEvent('mazlan_return_key_submit');
  };

  // Capture phase: React's listener sits on the <body> portal container and
  // clears the textarea before bubbling reaches document. Capture reads it first.
  document.addEventListener('keydown', trackEnterKeySubmit, true);

  return () => {
    uiVersionObserver?.disconnect();
    document.removeEventListener('keydown', trackEnterKeySubmit, true);
    teardownGlobalContext();
  };
};
